import React, { useEffect, useMemo, useRef, useState } from "react";
import { Gift } from "lucide-react";
import { resolveImageUrl } from "@/lib/images";
import { hexToRgb } from "@/lib/color";
import { wrapLabel } from "@/components/SpinWheel";

// "Nouveau design" wheel: thick rim with lights, radial labels, gift icon on
// winning segments, logo hub, side pointer, curved caption and a big button.
const VB_H = 440; // wheel 0..400 + room for the curved caption
const RIM = 196;
const DISC = 174;
const POINTER_DEG = 90; // pointer sits at 3 o'clock

const isLight = (hex) => {
  const [r, g, b] = hexToRgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b > 170;
};

function useSegments(segments, count, rimColor, showNames) {
  return useMemo(() => {
    const per = 360 / count;
    return Array.from({ length: count }, (_, i) => {
      const s = segments[i] || {};
      const a1 = (i * per - 90) * (Math.PI / 180);
      const a2 = ((i + 1) * per - 90) * (Math.PI / 180);
      const mid = ((i + 0.5) * per - 90) * (Math.PI / 180);
      const pt = (rad, a) => [200 + rad * Math.cos(a), 200 + rad * Math.sin(a)];
      const [x1, y1] = pt(DISC, a1);
      const [x2, y2] = pt(DISC, a2);
      const color = s.color || "#E2E8F0";
      const win = !!s.is_winning;
      const iconOnly = win && !showNames; // big picture / gift, no text
      const text = iconOnly ? "" : (wrapLabel((s.label || "").toUpperCase(), true)[0] || "").slice(0, 16);
      const textR = win ? 92 : 112;
      const avail = win ? 74 : 104;
      const iconR = iconOnly ? 118 : 146;
      const chord = 2 * iconR * Math.sin((per * Math.PI) / 360);
      const [tx, ty] = pt(textR, mid);
      const [ix, iy] = pt(iconR, mid);
      return {
        d: `M 200 200 L ${x1} ${y1} A ${DISC} ${DISC} 0 ${per > 180 ? 1 : 0} 1 ${x2} ${y2} Z`,
        color,
        ink: isLight(color) ? rimColor : "#ffffff",
        win,
        text,
        fs: Math.min(Math.max(8, 20 - count / 2), avail / Math.max(1, text.length * 0.66)),
        tx, ty, ix, iy,
        angle: (i + 0.5) * per,
        image: win ? resolveImageUrl(s.image_url) : null,
        icon: Math.min(chord * (iconOnly ? 0.62 : 0.5), iconOnly ? 54 : 30),
        img: Math.min(chord * (iconOnly ? 0.85 : 0.72), iconOnly ? 76 : 46),
      };
    });
  }, [segments, count, rimColor, showNames]);
}

function RimBack({ rimColor, count, curvedText }) {
  const dots = Math.max(12, count * 2);
  const r = 214;
  const c = Math.cos(Math.PI / 4) * r;
  return (
    <svg viewBox={`0 0 400 ${VB_H}`} className="absolute inset-0 w-full h-full" aria-hidden>
      <defs>
        <filter id="pw-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="9" floodOpacity="0.28" />
        </filter>
        <path id="pw-arc" d={`M ${200 - c} ${200 + c} A ${r} ${r} 0 0 0 ${200 + c} ${200 + c}`} fill="none" />
      </defs>
      <circle cx="200" cy="200" r={RIM} fill={rimColor} filter="url(#pw-shadow)" />
      {Array.from({ length: dots }, (_, i) => {
        const a = (i / dots) * 2 * Math.PI;
        return <circle key={i} cx={200 + 185 * Math.cos(a)} cy={200 + 185 * Math.sin(a)} r="4.4" fill="#ffffff" />;
      })}
      {curvedText && (
        <text fontFamily="Outfit, sans-serif" fontWeight="800" fontSize="17" letterSpacing="1" fill={rimColor} data-testid="premium-wheel-curved-text">
          <textPath href="#pw-arc" startOffset="50%" textAnchor="middle">{curvedText.toUpperCase()}</textPath>
        </text>
      )}
    </svg>
  );
}

function Disc({ segs, rotation, preview, rimColor, font }) {
  return (
    <svg
      viewBox="0 0 400 400"
      className="absolute left-0 top-0 w-full"
      style={{
        height: `${(400 / VB_H) * 100}%`,
        transformOrigin: "50% 50%",
        transform: `rotate(${rotation}deg)`,
        transition: preview ? "none" : "transform 5s cubic-bezier(0.22, 0.9, 0.25, 1)",
        willChange: "transform",
      }}
      data-testid="premium-wheel-disc"
    >
      {segs.map((s, i) => (
        <path key={i} d={s.d} fill={s.color} stroke={rimColor} strokeWidth="4" strokeLinejoin="round" />
      ))}
      {segs.map((s, i) => (
        <g key={`l${i}`} style={{ pointerEvents: "none" }}>
          {s.win && (
            <g transform={`rotate(${s.angle} ${s.ix} ${s.iy})`}>
              {s.image ? (
                <image href={s.image} x={s.ix - s.img / 2} y={s.iy - s.img / 2} width={s.img} height={s.img} preserveAspectRatio="xMidYMid meet" data-testid={`premium-wheel-image-${i}`} />
              ) : (
                <Gift x={s.ix - s.icon / 2} y={s.iy - s.icon / 2} width={s.icon} height={s.icon} color={s.ink} strokeWidth={2.2} data-testid={`premium-wheel-gift-${i}`} />
              )}
            </g>
          )}
          <text
            x={s.tx}
            y={s.ty}
            transform={`rotate(${s.angle > 180 ? s.angle + 90 : s.angle - 90} ${s.tx} ${s.ty})`}
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily={font.family || "Outfit, sans-serif"}
            fontWeight={font.weight || "800"}
            fontSize={s.fs * (font.scale || 1)}
            fill={s.ink}
            letterSpacing="0.5"
          >
            {s.text}
          </text>
        </g>
      ))}
    </svg>
  );
}

function PointerAndHub({ rimColor, pointerColor }) {
  return (
    <svg viewBox={`0 0 400 ${VB_H}`} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden>
      <path d="M 350 200 L 377 186 A 15.5 15.5 0 1 1 377 214 Z" fill={pointerColor} stroke="#ffffff" strokeWidth="2.5" data-testid="premium-wheel-pointer" />
      <circle cx="384" cy="200" r="5" fill="#ffffff" />
      <circle cx="200" cy="200" r="47" fill="#ffffff" />
      <circle cx="200" cy="200" r="42" fill={rimColor} />
    </svg>
  );
}

export default function SpinWheelPremium({
  segments = [],
  spinning = false,
  resultIndex = null,
  onSpin,
  disabled = false,
  preview = false,
  rimColor = "#1F4D3A",
  pointerColor = "#D62828",
  curvedText = "",
  buttonLabel = "",
  buttonStyle = null,
  hubLogo = null,
  labelFontFamily = null,
  labelFontWeight = null,
  labelFontScale = 1,
  showPrizeNames = true,
}) {
  const count = segments.length || 8;
  const [rotation, setRotation] = useState(0);
  const applied = useRef(null);
  const rim = rimColor || "#1F4D3A";
  const segs = useSegments(segments, count, rim, showPrizeNames);

  useEffect(() => {
    const key = `${resultIndex}-${spinning}`;
    if (!spinning || resultIndex === null || applied.current === key) return;
    const per = 360 / count;
    const target = resultIndex * per + per / 2;
    const currentMod = ((rotation % 360) + 360) % 360;
    const wantedMod = (((POINTER_DEG - target) % 360) + 360) % 360;
    applied.current = key;
    setRotation((r) => r + 6 * 360 + ((wantedMod - currentMod + 360) % 360));
  }, [spinning, resultIndex, count, rotation]);

  useEffect(() => {
    if (!spinning && resultIndex === null) applied.current = null;
  }, [spinning, resultIndex]);

  const btn = { background: pointerColor, color: "#ffffff", borderRadius: 9999, ...(buttonStyle || {}) };
  return (
    <div className={preview ? "w-full mx-auto select-none" : "w-full max-w-[520px] mx-auto select-none"} data-testid="premium-wheel">
      <div className="relative w-full" style={{ aspectRatio: `400 / ${VB_H}` }}>
        <RimBack rimColor={rim} count={count} curvedText={curvedText} />
        <Disc segs={segs} rotation={rotation} preview={preview} rimColor={rim} font={{ family: labelFontFamily, weight: labelFontWeight, scale: labelFontScale }} />
        <PointerAndHub rimColor={rim} pointerColor={pointerColor} />
        <button
          type="button"
          onClick={onSpin}
          disabled={preview || disabled || spinning}
          aria-label="spin"
          data-testid={preview ? undefined : "spin-wheel-btn"}
          className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center overflow-hidden enabled:hover:scale-105 enabled:active:scale-95 transition-transform"
          style={{ left: "50%", top: `${(200 / VB_H) * 100}%`, width: "21%", height: `${(84 / VB_H) * 100}%` }}
        >
          {hubLogo ? (
            <img src={hubLogo} alt="logo" className="h-[78%] w-[78%] rounded-full object-cover pointer-events-none" data-testid="spin-wheel-hub-logo" />
          ) : (
            <Gift className="h-1/2 w-1/2 text-white pointer-events-none" strokeWidth={2.2} />
          )}
        </button>
      </div>
      {!preview && (
        <div className="flex justify-center mt-4">
          <button
            type="button"
            onClick={onSpin}
            disabled={disabled || spinning}
            data-testid="premium-wheel-play-btn"
            className="px-12 py-4 text-lg font-extrabold uppercase tracking-wide shadow-[0_12px_30px_-8px_rgba(0,0,0,0.45)] enabled:hover:scale-[1.04] enabled:active:scale-95 transition-transform disabled:opacity-70"
            style={btn}
          >
            {spinning && resultIndex === null ? "…" : buttonLabel}
          </button>
        </div>
      )}
    </div>
  );
}
